import { CAST_AI } from '@cast/constants';

export const vpaSettingsConstants = {
  overhead: {
    title: 'Overhead',
    description:
      'Increase recommended request values by a percentage amount to provide extra capacity for the workload to handle further load increases.',
    docsLinkKey: 'docs_woop_vpa_overhead' as const,
    simulation: {
      description:
        'Shows how your overhead settings affect the final recommended request values.',
    },
  },
  stallDetection: {
    title: 'Stall detection',
    description:
      'Pressure Stall Information (PSI) provides accurate resource demand signals by measuring actual CPU contention, used as a secondary indicator for scaling sensitivity.',
    docsLinkKey: 'docs_woop_vpa_stall_detection' as const,
  },
  memoryGrowthDetection: {
    title: 'Memory growth detection',
    description:
      'Enable detection of unbounded memory growth, which caps memory recommendations at 1.5× current usage until the growth pattern stops.',
    docsLinkKey: 'docs_woop_vpa_memory_growth_detection' as const,
  },
  optimizedResources: {
    title: 'Selective optimization',
    docsLinkKey: 'docs_woop_vpa_selective_optimization' as const,
    optimizedResources: {
      title: 'Resources',
      description:
        "Resources that aren't selected will use default values. While recommendations will still be calculated, they won't be applied automatically.",
    },
  },
  excludedContainers: {
    title: 'Container exclusion',
    description:
      'You can selectively optimize only application containers. Dynamically injected sidecar containers will be excluded from automatic optimization and will keep their current settings.',
  },
  recommendationPercentile: {
    title: 'Recommendation percentile',
    description:
      'Define how close recommendations should be to observed workload resource usage. Higher percentiles lead to more generous resource requests.',
    docsLinkKey: 'docs_woop_vpa_percentile' as const,
    simulation: {
      description:
        'Shows how your percentile choices affect recommendations based on historical usage data.',
    },
  },
  workloadResourceLimits: {
    title: 'Workload resource limits',
    description:
      'Configure multipliers to automatically adjust container resource limits relative to requests. When set, multipliers determine how much higher limits will be compared to their corresponding requests.',
    docsLinkKey: 'docs_woop_vpa_resource_limits' as const,
  },
  workloadConstraints: {
    title: 'Workload autoscaler constraints',
    description:
      'Set the minimum and maximum CPU and memory requests the autoscaler can recommend for each container.',
    docsLinkKey: 'docs_woop_vpa_constraints' as const,
  },
  lookbackPeriod: {
    title: 'Look-back period',
    description:
      'The look-back period defines the timeframe the Workload autoscaler uses to observe CPU and memory usage when calculating scaling recommendations.',
    docsLinkKey: 'docs_woop_vpa_lookback' as const,
    simulation: {
      description:
        'Shows resource usage within your defined look-back period used for recommendations.',
    },
  },
  startupMetrics: {
    title: 'Startup metrics',
    description:
      'Some workloads have increased resource usage during startup, affecting autoscaling recommendations. You can ignore startup metrics to prevent inflated recommendations and unnecessary restarts.',
    docsLinkKey: 'docs_woop_vpa_startup_metrics' as const,
    noneLabel: 'Regular recommendations',
    noneDescription:
      'Includes startup metrics in recommendation calculation like any other period. May inflate values for workloads with high startup usage.',
    twoPhaseLabel: 'Startup recommendations',
    twoPhaseDescription:
      'Applies original CPU requests during startup, then switches to the optimized recommendation when the startup period ends.',
    ignoreLabel: 'Ignore startup metrics',
    ignoreDescription:
      'Excludes startup metrics from recommendation calculations, which prevents inflated values.',
    ignorePeriod: 'Ignore startup metrics',
  },
  autoscaler: {
    title: 'When to apply changes',
    description:
      'Configure the autoscaler operating mode to apply the recommendations.',
    docsLinkKey: 'docs_woop_vpa_apply_type' as const,
    immediatelyTooltip:
      'Apply changes as soon as thresholds are met, restarting pods.',
    deferredTooltip: 'Apply recommendations only on natural pod restarts.',
    eventTitle: 'Memory events',
    eventSubtitle:
      'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor.',
    downscaling: {
      title: 'Downscaling behavior',
      description:
        'Choose how request reductions are applied, separately from other changes.',
    },
    antiAffinity: {
      title: 'Anti-affinity constraints',
      description:
        'Workloads with hostname anti-affinity rules or host port requirements have special placement constraints. This setting overrides the recommendation apply type specifically for these constrained workloads.',
    },
    memoryEvent: {
      title: 'Memory events',
      description:
        'Choose how recommendations are applied when memory-related events like out-of-memory kills or memory pressure evictions are detected',
    },
    threshold: {
      title: 'Change sensitivity',
      description:
        'Apply changes immediately when the difference exceeds the set threshold, either as a fixed percentage or dynamically based on workload size.',
    },
    rolloutBehaviour: {
      title: 'Rollout behaviour',
      description:
        'Control how Workload Autoscaler applies recommendation rollouts to your pods.',
      evictPodsOneByOne: {
        title: 'Evict pods one by one',
        description:
          'Workload Autoscaler evicts one pod at a time, waiting for each to become healthy before proceeding to the next. Without this, multiple pods may be evicted in parallel.',
      },
      zeroDowntime: {
        title: 'Zero-downtime updates',
        description:
          'For single-replica workloads, temporarily scale to two replicas during resource adjustments to avoid downtime.',
      },
      delaySeconds: {
        title: 'Eviction delay',
        description:
          'Minimum time Workload Autoscaler waits between evictions. Applies whether or not pods are evicted one by one.',
      },
    },
  },
  customMetrics: {
    title: 'Custom metrics',
    description:
      'Configure how the Workload Autoscaler collects and uses JVM heap metrics to generate memory recommendations for Java workloads. Changes require a workload restart to take effect.',
    jvmHeapScaling: {
      title: 'JVM heap scaling',
      description:
        'Use JVM heap metrics instead of container memory metrics to generate memory recommendations.',
      alert:
        'If custom metrics become unavailable, Workload Autoscaler will revert to CPU and memory-based recommendations.',
    },
    autoInstrumentation: {
      title: 'Auto-instrumentation',
      description: `Automatically collect JVM heap metrics without manually configuring a Prometheus data source. ${CAST_AI} injects a Java agent into your Java workload runtimes to export metrics to ${CAST_AI}'s Prometheus instance.`,
      alert:
        'Enabling auto-instrumentation adds an init container to eligible workloads. Each workload needs to restart once for the change to take effect. Wait for a natural restart or trigger one manually.',
    },
  },
  confidence: {
    title: 'Confidence',
    description:
      'Set the minimum confidence level required before automatically applying resource recommendations to workloads assigned to this policy.',
    docsLinkKey: 'docs_woop_confidence' as const,
  },
  hpaConverter: {
    title: 'HPA converter',
    description:
      'Convert percentage-based HPA utilization targets into average value targets calculated from workload requests.',
    docsLinkKey: 'docs_woop_hpa' as const,
  },
  predictiveScaling: {
    title: 'Predictive scaling',
    description:
      'Enable ML-powered CPU scaling that forecasts usage up to 3 days ahead for eligible workloads assigned to this policy.',
    docsLinkKey: 'docs_woop_predictive_scaling' as const,
  },
};
